import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { useAccountFlow } from '@shared/hooks';
import { AmountUtils, cn, CurrencyUtils, FormatUtils } from '@shared/utils';
import { AccountStatus, type AccountResponse } from '@shared/api/rest/accounts';
import { ColorsEnum, Icon, Text } from '@shared/widgets';
import CardIcon from '@assets/icons/card.svg';

interface Props {
  account: AccountResponse;
}

export const AccountCardSmallTileVcfc = (props: Props) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { t } = useTranslation('accounts');

  const accountFlow = useAccountFlow(profileFeatureStore.data?.accountFlows, props.account.id);
  const progress = useMemo(() => {
    if (props.account.balance) {
      return props.account.balance / (props.account.creditLimit / 100);
    } else return 0;
  }, [props.account]);

  if (accountFlow?.view === false) return null;
  return (
    <div
      className={cn('flex flex-col rounded-xl bg-white dark:bg-soft-black', props.account.status === AccountStatus.DELINQUENT && 'border border-red')}
    >
      <div className='p-4'>
        <div className='flex w-full justify-between'>
          <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(props.account.balance, props.account.currency)}</Text>
          <div
            className={cn(
              'flex h-[29px] min-w-[65px] items-center justify-center gap-2 rounded-[45px] px-2 text-white',
              props.account.status === AccountStatus.ACTIVE && 'bg-primary dark:bg-white',
              [AccountStatus.BLOCKED, AccountStatus.DELINQUENT].includes(props.account.status) && 'bg-red',
              [AccountStatus.CLOSED, AccountStatus.DORMANT].includes(props.account.status) && 'bg-light/70',
            )}
          >
            <Text weight='medium' color={ColorsEnum.PrimaryReverse}>
              {CurrencyUtils.getSymbolByCode(props.account.currency)}
            </Text>
            <Text className='text-[10px]' weight='semibold' color={ColorsEnum.PrimaryReverse}>
              {t(`statuses.account.${props.account.status}`, { ns: 'common' })}
            </Text>
          </div>
        </div>
        <Text className='mt-2' weight='medium' size='xs' color={ColorsEnum.Secondary}>
          {props.account.name}
        </Text>
        <Text className='mt-1 text-[10px]' weight='semibold' color={ColorsEnum.Gray}>
          {props.account.productName}
        </Text>
        <Text className='mt-1' weight='medium'>
          {props.account.name}
        </Text>
      </div>
      <div className='mx-5 my-3 h-[1px] bg-gray-black' />
      <div className='flex flex-col px-5 py-4'>
        <div className='relative h-1 w-full rounded bg-light'>
          <div className='absolute left-0 h-full rounded bg-primary dark:bg-white' style={{ width: `${progress}%` }}></div>
        </div>
        <div className='mt-2 flex justify-between'>
          <Text size='sm' color={ColorsEnum.Secondary}>
            {t('card.tile.fields.spendingLimit')}
          </Text>
          <Text weight='medium' size='xs'>
            {`${AmountUtils.transformToAmountWithCurrency(props.account.balance, props.account.currency)} / ${AmountUtils.transformToAmountWithCurrency(props.account.creditLimit, props.account.currency)}`}
          </Text>
        </div>
      </div>
      <div
        className={cn(
          'rounded-lg p-4',
          props.account.status === AccountStatus.ACTIVE && 'bg-primary',
          [AccountStatus.BLOCKED, AccountStatus.DELINQUENT].includes(props.account.status) && 'bg-red/10',
          [AccountStatus.CLOSED, AccountStatus.DORMANT].includes(props.account.status) && 'bg-light/70',
        )}
      >
        <div className='flex justify-between'>
          <Text size='sm' color={props.account.status === AccountStatus.ACTIVE ? ColorsEnum.White : ColorsEnum.Black}>
            {t('card.tile.fields.accountNumber')}
          </Text>
          <Text weight='medium' size='xs' color={props.account.status === AccountStatus.ACTIVE ? ColorsEnum.White : ColorsEnum.Black}>
            {FormatUtils.toSliceNumber(props.account.accountNumber)}
          </Text>
        </div>
        <div className='mt-2 flex justify-between'>
          <Text size='sm' color={props.account.status === AccountStatus.ACTIVE ? ColorsEnum.White : ColorsEnum.Black}>
            {t('card.tile.fields.cards')}
          </Text>
          <div className='flex gap-1'>
            <Text weight='medium' size='xs' color={props.account.status === AccountStatus.ACTIVE ? ColorsEnum.White : ColorsEnum.Black}>
              {props.account.cardsCount}
            </Text>
            <Icon
              icon={<CardIcon className={props.account.status === AccountStatus.ACTIVE ? 'text-white' : 'text-soft-black'} width='16' height='16' />}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
